/**
 * § 7.15 Phase 2 — Plan-then-execute progress renderer.
 *
 * Renders user-perceptible progress for a multi-step plan
 * emitted by the backend's chat_stream when ≥2 action tools
 * accumulate. The patentable mechanism is the user-visible
 * plan-as-first-class-object: the user sees the plan + its
 * step-by-step state instead of a 30s opaque wait.
 *
 * Empirical anchor: 30s blank wait on
 * generate_decomposition_plan (audit F1) — with this component,
 * the user sees plan.started + per-step progress within ms of
 * the backend yielding the events.
 *
 * The backend SSE shape this consumes:
 *   - plan.started        { plan_id, summary, step_count }
 *   - plan.step.started   { plan_id, step_id, description, tool }
 *   - plan.step.completed { plan_id, step_id, tool }
 *   - plan.step.failed    { plan_id, step_id, tool }
 *   - plan_summary        on the done event
 */

export type PlanStepStatus = "pending" | "running" | "completed" | "failed";

export interface PlanStep {
  step_id: string;
  description: string;
  tool: string;
  status: PlanStepStatus;
}

export interface PlanState {
  plan_id: string;
  summary: string;
  steps: PlanStep[];
}

interface Props {
  plan: PlanState;
}

const STATUS_LABEL: Record<PlanStepStatus, string> = {
  pending: "…",
  running: "→",
  completed: "✓",
  failed: "✗",
};

const STATUS_CLASS: Record<PlanStepStatus, string> = {
  pending: "text-slate-600 dark:text-slate-300",
  running: "text-blue-600 dark:text-blue-400",
  completed: "text-emerald-700 dark:text-emerald-400",
  failed: "text-red-600 dark:text-red-400",
};

export function FelixChatPlan({ plan }: Props) {
  return (
    <div
      // KI-0230 belt-and-suspenders: the parent message column now
      // hoists wrap CSS for all descendants, but adding min-w-0 +
      // max-w-full here makes this component safe to mount anywhere
      // (e.g. dashboards) without inheriting the column's contract.
      className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm min-w-0 max-w-full dark:border-slate-700 dark:bg-slate-900/40"
      data-testid="felix-plan"
      data-plan-id={plan.plan_id}
    >
      <p className="mb-2 text-xs font-medium text-slate-600 dark:text-slate-300">
        {plan.summary}
      </p>
      <ul className="space-y-1">
        {plan.steps.map((step) => (
          <li
            key={step.step_id}
            data-testid={`felix-plan-step-${step.step_id}`}
            data-status={step.status}
            className="flex items-center gap-2"
          >
            <span
              className={`inline-block w-4 text-center font-mono ${STATUS_CLASS[step.status]}`}
              aria-label={step.status}
            >
              {STATUS_LABEL[step.status]}
            </span>
            <span className="text-slate-700 dark:text-slate-200">
              {step.description}
            </span>
            <span className="text-xs text-slate-600 dark:text-slate-300">({step.tool})</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
